import { AlertCircle, RefreshCw, X } from "lucide-react";
import { useState, useEffect, useLayoutEffect, useRef, useMemo } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { ChevronUp, ChevronDown, ChevronsUpDown } from "lucide-react";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
  getSortedRowModel,
  type SortingState,
  type Column,
} from "@tanstack/react-table";

import RetailerHealthPanel from "./RetailerHealthPanel";
import {
  getRetailerAnalytics,
  type AnalyticsRetailerType,
  type RetailerAnalyticsData,
  type StoreAnalytics,
} from "../../services/RetailerAnalyticsService";

type ColumnSizing = {
  [key: string]: number;
};

const BREAKDOWN_COLORS = ["#ef4444", "#f59e0b", "#3b82f6", "#10b981"];

const RETAILER_OPTIONS: { key: AnalyticsRetailerType; label: string }[] = [
  { key: "walmart", label: "Walmart" },
  { key: "amazon", label: "Amazon" },
  { key: "home_depot", label: "Home Depot" },
  { key: "dollar_general", label: "Dollar General" },
  { key: "target", label: "Target" },
  { key: "bestbuy", label: "Best Buy" },
];

const getRetailerPath = (retailer: AnalyticsRetailerType): string => {
  const pathMap: Record<AnalyticsRetailerType, string> = {
    walmart: "walmart",
    walmart_online: "walmart-online",
    amazon: "amazon",
    home_depot: "home_depot",
    dollar_general: "dollar-general",
    target: "target",
    target_online: "target-online",
    bestbuy: "bestbuy",
    costco: "costco",
    lowes: "lowes",
    kohls: "kohls",
  };
  return pathMap[retailer];
};

const columnHelper = createColumnHelper<StoreAnalytics>();

function SortHeader({ column, children, align = "right" }: {
  column: Column<StoreAnalytics, unknown>;
  children: React.ReactNode;
  align?: "left" | "right";
}) {
  return (
    <button
      className={`flex items-center gap-1 uppercase tracking-[0.14em] font-bold hover:text-gray-600 dark:hover:text-dark-200 transition-colors ${align === "right" ? "justify-end w-full" : ""}`}
      onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
    >
      {children}
      {column.getIsSorted() === "asc" ? (
        <ChevronUp className="w-3 h-3 text-red-600" />
      ) : column.getIsSorted() === "desc" ? (
        <ChevronDown className="w-3 h-3 text-red-600" />
      ) : (
        <ChevronsUpDown className="w-3 h-3 opacity-50" />
      )}
    </button>
  );
}

const numCell = (value: string | number) => (
  <span className="text-xs font-mono tabular-nums text-gray-700 dark:text-dark-100 text-right block">{value}</span>
);

export default function AnalyticsTab() {
  const [selectedRetailer, setSelectedRetailer] =
    useState<AnalyticsRetailerType>("walmart");
  const [analytics, setAnalytics] = useState<RetailerAnalyticsData | null>(
    null
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [sorting, setSorting] = useState<SortingState>([
    { id: "productsUploaded", desc: true },
  ]);
  const [columnWidths, setColumnWidths] = useState<ColumnSizing>({});
  const [storeIdSearch, setStoreIdSearch] = useState<string>("");

  const tableParentRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLTableRowElement>(null);

  useEffect(() => {
    loadAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRetailer]);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getRetailerAnalytics(selectedRetailer);
      setAnalytics(data);
      setLastUpdated(new Date());
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to load analytics";
      setError(errorMessage);
      console.error("Error loading analytics:", err);
    } finally {
      setLoading(false);
    }
  };

  const columns = useMemo(
    () => [
      columnHelper.accessor("storeId", {
        header: ({ column }) => <SortHeader column={column} align="left">Store</SortHeader>,
        cell: (info) => {
          const storeId = info.getValue();
          const retailerPath = getRetailerPath(selectedRetailer);
          return (
            <a
              href={`/${retailerPath}/products?storeId=${storeId}`}
              className="text-xs font-mono font-semibold text-blue-600 dark:text-blue-400 hover:underline"
            >
              {storeId}
            </a>
          );
        },
      }),
      columnHelper.accessor("productsUploaded", {
        header: ({ column }) => <SortHeader column={column}>Uploaded</SortHeader>,
        cell: (info) => numCell(info.getValue().toLocaleString()),
      }),
      columnHelper.accessor("totalProductsFirstInserted", {
        header: ({ column }) => <SortHeader column={column}>First inserted</SortHeader>,
        cell: (info) => numCell(info.getValue().toLocaleString()),
      }),
      columnHelper.accessor("averageDiscount", {
        header: ({ column }) => <SortHeader column={column}>Avg discount</SortHeader>,
        cell: (info) => numCell(`${info.getValue().toFixed(1)}%`),
      }),
      columnHelper.accessor("usersSearched", {
        header: ({ column }) => <SortHeader column={column}>Users</SortHeader>,
        cell: (info) => numCell(info.getValue()),
      }),
      columnHelper.accessor("categoriesExpected", {
        header: ({ column }) => <SortHeader column={column}>Cats expected</SortHeader>,
        cell: (info) => numCell(info.getValue()),
      }),
      columnHelper.display({
        id: "categoriesFound",
        header: ({ column }) => <SortHeader column={column}>Cats found</SortHeader>,
        cell: (info) =>
          numCell(Object.keys(info.row.original.productsFoundPerCategory || {}).length),
        sortingFn: (rowA, rowB) => {
          const aValue = Object.keys(
            rowA.original.productsFoundPerCategory || {}
          ).length;
          const bValue = Object.keys(
            rowB.original.productsFoundPerCategory || {}
          ).length;
          return aValue - bValue;
        },
      }),
      columnHelper.display({
        id: "status",
        header: "Status",
        cell: (info) => {
          const hasIssues = info.row.original.productsUploaded === 0;
          return (
            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-600 dark:text-dark-200">
              <span className={`w-1.5 h-1.5 rounded-full ${hasIssues ? "bg-amber-400" : "bg-emerald-500"}`} />
              {hasIssues ? "Issue" : "Active"}
            </span>
          );
        },
      }),
    ],
    [selectedRetailer]
  );

  // Initialize table data with search filter
  const tableData = useMemo(() => {
    const stores = analytics?.storeAnalytics || [];
    if (!storeIdSearch.trim()) {
      return stores;
    }
    const searchLower = storeIdSearch.toLowerCase().trim();
    return stores.filter((store) =>
      store.storeId.toLowerCase().includes(searchLower)
    );
  }, [analytics?.storeAnalytics, storeIdSearch]);

  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: {
      sorting,
    },
    onSortingChange: setSorting,
  });

  const virtualizer = useVirtualizer({
    count: table.getRowModel().rows.length,
    getScrollElement: () => tableParentRef.current,
    estimateSize: () => 40,
    overscan: 5,
  });

  // Measure header column widths to maintain alignment
  useLayoutEffect(() => {
    if (headerRef.current && tableData.length > 0) {
      const headerCells = headerRef.current.querySelectorAll("th");
      const widths: ColumnSizing = {};
      const headerGroup = table.getHeaderGroups()[0];
      if (headerGroup) {
        headerCells.forEach((cell, index) => {
          const header = headerGroup.headers[index];
          if (header) {
            widths[header.id] = cell.getBoundingClientRect().width;
          }
        });
        setColumnWidths(widths);
      }
    }
  }, [tableData.length, table]);

  const productBreakdownData = useMemo(() => {
    if (!analytics?.metadata?.productBreakdown) return [];
    return [
      {
        name: "0–10 products",
        value: analytics.metadata.productBreakdown[0] || 0,
      },
      {
        name: "11–50 products",
        value: analytics.metadata.productBreakdown[1] || 0,
      },
      {
        name: "51–100 products",
        value: analytics.metadata.productBreakdown[2] || 0,
      },
      {
        name: "100+ products",
        value: analytics.metadata.productBreakdown[3] || 0,
      },
    ].filter((item) => item.value > 0);
  }, [analytics?.metadata?.productBreakdown]);

  const topStoresByProducts = useMemo(() => {
    if (!analytics?.storeAnalytics || analytics.storeAnalytics.length === 0)
      return [];
    return [...analytics.storeAnalytics]
      .sort((a, b) => b.productsUploaded - a.productsUploaded)
      .slice(0, 10)
      .map((store) => ({
        storeId: store.storeId,
        productsUploaded: store.productsUploaded,
        averageDiscount: store.averageDiscount,
        usersSearched: store.usersSearched,
      }));
  }, [analytics?.storeAnalytics]);

  const storesWithIssues = useMemo(() => {
    if (!analytics?.storeAnalytics || analytics.storeAnalytics.length === 0)
      return [];
    return analytics.storeAnalytics.filter(
      (store) => store.productsUploaded === 0
    );
  }, [analytics?.storeAnalytics]);

  const topCategories = useMemo(() => {
    if (!analytics?.storeAnalytics || analytics.storeAnalytics.length === 0)
      return [];
    const categoryCounts: Record<string, number> = {};
    analytics.storeAnalytics.forEach((store) => {
      if (store.productsFoundPerCategory) {
        Object.entries(store.productsFoundPerCategory).forEach(
          ([category, count]) => {
            categoryCounts[category] = (categoryCounts[category] || 0) + count;
          }
        );
      }
    });
    return Object.entries(categoryCounts)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 10)
      .map(([name, value]) => ({ name, value }));
  }, [analytics?.storeAnalytics]);

  // Early returns must come after all hooks
  if (loading && !analytics) {
    return (
      <div className="flex items-center justify-center py-16">
        <RefreshCw className="w-8 h-8 animate-spin text-gray-400 dark:text-dark-400" />
      </div>
    );
  }

  // The daily S3 snapshot is a nice-to-have; live retailer health is the signal
  // that matters and does not depend on that file existing. Failing to load the
  // snapshot degrades this tab rather than emptying it.
  if (!analytics) {
    return (
      <div className="space-y-4">
        <RetailerHealthPanel />
        {error && (
          <div className="bg-white dark:bg-dark-800 rounded-2xl ring-1 ring-amber-200 dark:ring-amber-900 shadow-sm p-6">
            <div className="flex items-center gap-2 mb-1">
              <AlertCircle className="w-4 h-4 text-amber-600 dark:text-amber-400" />
              <h3 className="text-[13px] font-bold text-gray-900 dark:text-white">
                Daily snapshot unavailable
              </h3>
            </div>
            <p className="text-xs text-gray-500 dark:text-dark-300 mb-4">
              {error} Live retailer health above is unaffected.
            </p>
            <button
              onClick={loadAnalytics}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-gray-900 dark:bg-white text-white dark:text-dark-900 hover:opacity-90 transition-opacity"
            >
              Retry
            </button>
          </div>
        )}
      </div>
    );
  }

  const retailerLabel = RETAILER_OPTIONS.find((r) => r.key === selectedRetailer)?.label || selectedRetailer;
  const activeStores = analytics?.storeAnalytics && analytics.storeAnalytics.length > 0
    ? analytics.storeAnalytics.length - storesWithIssues.length
    : 0;
  const avgDiscount = analytics?.storeAnalytics && analytics.storeAnalytics.length > 0
    ? `${(analytics.storeAnalytics.reduce((sum, s) => sum + (s.averageDiscount || 0), 0) / analytics.storeAnalytics.length).toFixed(1)}%`
    : "0%";

  return (
    <div className="space-y-4">
      <RetailerHealthPanel />
      {/* Header */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="leading-tight mr-auto">
          <h2 className="text-[13px] font-bold text-gray-900 dark:text-white">{retailerLabel} analytics</h2>
          {lastUpdated && (
            <p className="text-[11px] text-gray-400 dark:text-dark-400 font-mono">
              Updated {lastUpdated.toLocaleTimeString()}
            </p>
          )}
        </div>
        <div className="flex items-center p-0.5 rounded-lg bg-gray-100 dark:bg-dark-700 overflow-x-auto scrollbar-hide">
          {RETAILER_OPTIONS.map((r) => (
            <button
              key={r.key}
              onClick={() => setSelectedRetailer(r.key)}
              className={`shrink-0 px-2.5 py-1.5 rounded-md text-xs font-semibold transition-colors ${selectedRetailer === r.key
                ? "bg-white dark:bg-dark-800 text-gray-900 dark:text-white shadow-sm"
                : "text-gray-500 dark:text-dark-300 hover:text-gray-700 dark:hover:text-dark-100"
                }`}
            >
              {r.label}
            </button>
          ))}
        </div>
        <button
          onClick={loadAnalytics}
          disabled={loading}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-gray-600 dark:text-dark-200 ring-1 ring-gray-200 dark:ring-dark-600 hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {/* Key metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Products uploaded"
          value={analytics?.metadata?.totalProductsUploaded?.toLocaleString() || "0"}
          subtitle={`${analytics?.metadata?.totalProductsFirstInserted?.toLocaleString() || 0} first inserted`}
        />
        <MetricCard
          title="Users"
          value={analytics?.metadata?.totalUsers?.toLocaleString() || "0"}
          subtitle={`${analytics?.metadata?.usersWithProducts || 0} with products`}
        />
        <MetricCard
          title="Active stores"
          value={activeStores.toLocaleString()}
          subtitle={`${storesWithIssues.length} with issues`}
          warn={storesWithIssues.length > 0}
        />
        <MetricCard
          title="Avg discount"
          value={avgDiscount}
          subtitle="Across all stores"
        />
      </div>

      {/* Stores with issues */}
      {storesWithIssues.length > 0 && (
        <div className="bg-amber-50/70 dark:bg-amber-900/10 rounded-2xl ring-1 ring-amber-200 dark:ring-amber-900 p-4">
          <div className="flex items-center gap-2 mb-1">
            <AlertCircle className="w-4 h-4 text-amber-500" />
            <h3 className="text-[13px] font-bold text-amber-800 dark:text-amber-300">
              {storesWithIssues.length} store{storesWithIssues.length !== 1 ? "s" : ""} with 0 products in the last 24h
            </h3>
          </div>
          <div className="flex flex-wrap gap-1.5 mt-2.5">
            {storesWithIssues.slice(0, 20).map((store) => (
              <a
                key={store.storeId}
                href={`/${getRetailerPath(selectedRetailer)}/products?storeId=${store.storeId}`}
                className="px-2 py-0.5 rounded-md bg-white dark:bg-dark-800 ring-1 ring-amber-200 dark:ring-amber-900 text-amber-800 dark:text-amber-300 text-xs font-mono hover:ring-amber-400 transition-shadow"
              >
                {store.storeId}
              </a>
            ))}
            {storesWithIssues.length > 20 && (
              <span className="px-2 py-0.5 text-amber-700 dark:text-amber-300 text-xs font-mono">
                +{storesWithIssues.length - 20} more
              </span>
            )}
          </div>
        </div>
      )}

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
        {/* Product distribution */}
        <div className="bg-white dark:bg-dark-800 rounded-2xl ring-1 ring-gray-200 dark:ring-dark-700 shadow-sm p-4">
          <h3 className="text-[13px] font-bold text-gray-900 dark:text-white mb-3">User product distribution</h3>
          {productBreakdownData.length > 0 ? (
            <div className="space-y-2.5">
              {productBreakdownData.map((item, index) => {
                const total = productBreakdownData.reduce((sum, i) => sum + i.value, 0);
                const percentage = ((item.value / total) * 100).toFixed(1);
                return (
                  <div key={item.name}>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="flex items-center gap-1.5 font-medium text-gray-700 dark:text-dark-100">
                        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: BREAKDOWN_COLORS[index % BREAKDOWN_COLORS.length] }} />
                        {item.name}
                      </span>
                      <span className="font-mono tabular-nums text-gray-700 dark:text-dark-100">
                        {item.value.toLocaleString()} <span className="text-gray-400 dark:text-dark-400">({percentage}%)</span>
                      </span>
                    </div>
                    <div className="w-full bg-gray-100 dark:bg-dark-700 rounded-full h-1.5">
                      <div
                        className="h-1.5 rounded-full transition-all"
                        style={{ width: `${percentage}%`, backgroundColor: BREAKDOWN_COLORS[index % BREAKDOWN_COLORS.length] }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="h-32 flex items-center justify-center text-xs text-gray-400 dark:text-dark-400">
              No data available
            </div>
          )}
        </div>

        {/* Top categories */}
        <div className="bg-white dark:bg-dark-800 rounded-2xl ring-1 ring-gray-200 dark:ring-dark-700 shadow-sm p-4">
          <h3 className="text-[13px] font-bold text-gray-900 dark:text-white mb-3">Top categories by products found</h3>
          {topCategories.length > 0 ? (
            <div className="space-y-2.5">
              {topCategories.map((category) => {
                const maxValue = Math.max(...topCategories.map((c) => c.value));
                const percentage = (category.value / maxValue) * 100;
                return (
                  <div key={category.name}>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="font-medium text-gray-700 dark:text-dark-100 truncate flex-1 mr-4">
                        {category.name}
                      </span>
                      <span className="font-mono tabular-nums text-gray-700 dark:text-dark-100">
                        {category.value.toLocaleString()}
                      </span>
                    </div>
                    <div className="w-full bg-gray-100 dark:bg-dark-700 rounded-full h-1.5">
                      <div className="bg-red-500 h-1.5 rounded-full transition-all" style={{ width: `${percentage}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="h-32 flex items-center justify-center text-xs text-gray-400 dark:text-dark-400">
              No data available
            </div>
          )}
        </div>
      </div>

      {/* Top stores */}
      <div className="bg-white dark:bg-dark-800 rounded-2xl ring-1 ring-gray-200 dark:ring-dark-700 shadow-sm p-4">
        <h3 className="text-[13px] font-bold text-gray-900 dark:text-white mb-3">Top stores by products uploaded</h3>
        {topStoresByProducts.length > 0 ? (
          <div className="space-y-1">
            {topStoresByProducts.map((store, index) => {
              const maxProducts = Math.max(...topStoresByProducts.map((s) => s.productsUploaded));
              const productsPercentage = (store.productsUploaded / maxProducts) * 100;
              return (
                <a
                  key={store.storeId}
                  href={`/${getRetailerPath(selectedRetailer)}/products?storeId=${store.storeId}`}
                  className="grid items-center gap-3 px-2 py-1.5 -mx-2 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-700/50 transition-colors"
                  style={{ gridTemplateColumns: "1.5rem 5rem minmax(0,1fr) 5.5rem 3.5rem 4rem" }}
                >
                  <span className="text-[11px] font-mono text-gray-300 dark:text-dark-500 text-right">{index + 1}</span>
                  <span className="text-xs font-mono font-semibold text-blue-600 dark:text-blue-400">#{store.storeId}</span>
                  <span className="block bg-gray-100 dark:bg-dark-700 rounded-full h-1.5">
                    <span className="block bg-blue-500 h-1.5 rounded-full" style={{ width: `${productsPercentage}%` }} />
                  </span>
                  <span className="text-xs font-mono tabular-nums font-semibold text-gray-800 dark:text-dark-50 text-right">{store.productsUploaded.toLocaleString()}</span>
                  <span className="text-xs font-mono tabular-nums text-gray-500 dark:text-dark-300 text-right" title="Users searched">{store.usersSearched}</span>
                  <span className="text-xs font-mono tabular-nums text-gray-500 dark:text-dark-300 text-right" title="Average discount">{store.averageDiscount.toFixed(1)}%</span>
                </a>
              );
            })}
            <div
              className="grid items-center gap-3 px-2 pt-1 text-[10px] font-bold uppercase tracking-[0.14em] text-gray-300 dark:text-dark-500"
              style={{ gridTemplateColumns: "1.5rem 5rem minmax(0,1fr) 5.5rem 3.5rem 4rem" }}
            >
              <span /><span /><span />
              <span className="text-right">Uploaded</span>
              <span className="text-right">Users</span>
              <span className="text-right">Disc</span>
            </div>
          </div>
        ) : (
          <div className="h-32 flex items-center justify-center text-xs text-gray-400 dark:text-dark-400">
            No data available
          </div>
        )}
      </div>

      {/* Stores table */}
      <div className="bg-white dark:bg-dark-800 rounded-2xl ring-1 ring-gray-200 dark:ring-dark-700 shadow-sm overflow-hidden">
        <div className="flex flex-wrap items-center gap-3 px-4 pt-4 pb-3 border-b border-gray-100 dark:border-dark-700">
          <h3 className="text-[13px] font-bold text-gray-900 dark:text-white mr-auto">
            Store analytics
            <span className="ml-2 text-[11px] font-mono font-normal text-gray-400 dark:text-dark-400">
              {storeIdSearch ? `${tableData.length}/` : ""}{analytics?.storeAnalytics?.length || 0}
            </span>
          </h3>
          <div className="relative">
            <input
              type="text"
              value={storeIdSearch}
              onChange={(e) => setStoreIdSearch(e.target.value)}
              placeholder="Filter by store ID…"
              className="w-48 pl-3 pr-8 py-1.5 text-xs font-mono rounded-lg bg-gray-50 dark:bg-dark-700/60 ring-1 ring-transparent focus:ring-2 focus:ring-red-500/60 focus:bg-white dark:focus:bg-dark-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-dark-400 focus:outline-none transition-all"
            />
            {storeIdSearch && (
              <button onClick={() => setStoreIdSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-dark-200">
                <X size={13} />
              </button>
            )}
          </div>
        </div>
        {tableData && tableData.length > 0 ? (
          <div
            ref={tableParentRef}
            className="overflow-auto scrollbar-modern"
            style={{ maxHeight: "560px" }}
          >
            <div
              style={{
                height: `${virtualizer.getTotalSize()}px`,
                width: "100%",
                position: "relative",
              }}
            >
              <table className="min-w-full">
                <thead className="bg-white dark:bg-dark-800 sticky top-0 z-10 shadow-[0_1px_0_0_theme(colors.gray.100)] dark:shadow-[0_1px_0_0_theme(colors.dark.700)]">
                  <tr ref={headerRef}>
                    {table.getHeaderGroups()[0]?.headers.map((header) => {
                      const isNumericColumn = [
                        "productsUploaded",
                        "totalProductsFirstInserted",
                        "averageDiscount",
                        "usersSearched",
                        "categoriesExpected",
                        "categoriesFound",
                      ].includes(header.id);
                      return (
                        <th
                          key={header.id}
                          className={`px-4 py-2 text-[10px] font-bold uppercase tracking-[0.14em] text-gray-400 dark:text-dark-400 ${isNumericColumn ? "text-right" : "text-left"}`}
                          style={
                            columnWidths[header.id]
                              ? { width: `${columnWidths[header.id]}px` }
                              : undefined
                          }
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {/* Spacer row to maintain column widths */}
                  {tableData.length > 0 && (
                    <tr
                      aria-hidden="true"
                      style={{ height: 0, visibility: "hidden" }}
                    >
                      {table.getHeaderGroups()[0]?.headers.map((header) => (
                        <td
                          key={header.id}
                          style={
                            columnWidths[header.id]
                              ? {
                                  width: `${columnWidths[header.id]}px`,
                                  padding: 0,
                                }
                              : { padding: 0 }
                          }
                        />
                      ))}
                    </tr>
                  )}
                  {virtualizer.getVirtualItems().map((virtualItem) => {
                    const row = table.getRowModel().rows[virtualItem.index];
                    const hasIssues = row.original.productsUploaded === 0;
                    return (
                      <tr
                        key={row.id}
                        data-index={virtualItem.index}
                        ref={virtualizer.measureElement}
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          transform: `translateY(${virtualItem.start}px)`,
                        }}
                        className={`border-b border-gray-50 dark:border-dark-700/60 ${hasIssues ? "bg-amber-50/60 dark:bg-amber-900/10" : ""}`}
                      >
                        {row.getVisibleCells().map((cell) => {
                          const isNumericColumn = [
                            "productsUploaded",
                            "totalProductsFirstInserted",
                            "averageDiscount",
                            "usersSearched",
                            "categoriesExpected",
                            "categoriesFound",
                          ].includes(cell.column.id);
                          return (
                            <td
                              key={cell.id}
                              className={`px-4 py-2 whitespace-nowrap ${isNumericColumn ? "text-right" : "text-left"}`}
                              style={
                                columnWidths[cell.column.id]
                                  ? {
                                      width: `${columnWidths[cell.column.id]}px`,
                                    }
                                  : undefined
                              }
                            >
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="p-10 text-center text-xs text-gray-400 dark:text-dark-400">
            No store analytics data available
          </div>
        )}
      </div>
    </div>
  );
}

interface MetricCardProps {
  title: string;
  value: string;
  subtitle: string;
  warn?: boolean;
}

function MetricCard({ title, value, subtitle, warn }: MetricCardProps) {
  return (
    <div className="bg-white dark:bg-dark-800 rounded-2xl ring-1 ring-gray-200 dark:ring-dark-700 shadow-sm px-4 py-3.5">
      <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-gray-400 dark:text-dark-400">{title}</p>
      <p className="text-2xl font-mono font-bold tabular-nums text-gray-900 dark:text-white leading-tight mt-0.5">{value}</p>
      <p className={`text-[11px] mt-0.5 ${warn ? "text-amber-600 dark:text-amber-400 font-medium" : "text-gray-400 dark:text-dark-400"}`}>{subtitle}</p>
    </div>
  );
}
